'use client';

import { useIsMobile } from '@/components/hooks/use-mobile';
import { usePricingBandPersistence } from '@/components/hooks/use-pricing-band-persistence';
import type { DropdownOption } from '@/components/ui/dropdown';
import { Dropdown } from '@/components/ui/dropdown';
import { generatePricingUrls } from '@/lib/utils/pricing';
import { isNearTop, scrollToElement } from '@/lib/utils/scroll-utils';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import styles from './pricing.module.scss';

export type PricingBand = {
  min: number;
  max: number | null;
  code?: string;
  label?: string;
};

export type PricingBandSelectorProps = {
  pricingBands: PricingBand[];
  categorySlug?: string;
  subcategorySlug?: string;
  selectedBandId?: string;
  navigation?: 'pricing' | 'current';
};

export const PricingBandSelector = ({
  pricingBands,
  categorySlug,
  subcategorySlug,
  navigation = 'pricing',
}: PricingBandSelectorProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const { lastSelectedBand, saveBandSelection } = usePricingBandPersistence();
  const isMobile = useIsMobile();
  const searchParams = useSearchParams();
  const selectedBandId = searchParams.get('band');

  // Determine the effective selected band (URL param takes precedence over stored selection)
  const effectiveSelectedBand = selectedBandId || lastSelectedBand || '';

  const buildBandUrl = useCallback(
    (bandCode: string) => {
      if (navigation === 'current') {
        const params = new URLSearchParams(searchParams);
        params.set('band', bandCode);
        return `${pathname}?${params.toString()}`;
      }

      if (subcategorySlug && categorySlug) {
        return generatePricingUrls.getBandUrl(categorySlug, subcategorySlug, bandCode);
      }

      return null;
    },
    [categorySlug, navigation, pathname, searchParams, subcategorySlug],
  );

  // Handle band selection and navigation
  const handleBandChange = useCallback(
    (bandCode: string) => {
      // Save the selection to session storage
      saveBandSelection(bandCode);

      const bandUrl = buildBandUrl(bandCode);
      if (bandUrl) {
        router.push(bandUrl, { scroll: false });

        // Scroll to pricing section to reveal pricing cards

        setTimeout(() => {
          if (isNearTop(isMobile ? 900 : 500)) {
            scrollToElement('pricing-band-selector', isMobile ? 80 : 250);
          }
        }, 100);
      }
    },
    [saveBandSelection, buildBandUrl, router, isMobile],
  );

  // Auto-redirect to the last selected band if no band is currently selected but one exists in storage
  useEffect(() => {
    if (lastSelectedBand && !selectedBandId) {
      const bandUrl = buildBandUrl(lastSelectedBand);
      if (bandUrl) {
        router.push(bandUrl, { scroll: false });
      }
    }
  }, [lastSelectedBand, selectedBandId, buildBandUrl, router]);

  // Transform priceBands to dropdown options
  const turnoverOptions: DropdownOption[] = pricingBands.map((band) => {
    const bandCode = band.code || band.min.toString();
    return {
      value: bandCode,
      label:
        band.label ||
        (band.max === null
          ? `£${band.min.toLocaleString()}+`
          : `£${band.min.toLocaleString()} - £${band.max.toLocaleString()}`),
    };
  });

  return (
    <div id="pricing-band-selector" className={styles.component_band}>
      <p className={styles.component_band_title}>To get started, select your turnover:</p>
      <Dropdown
        options={turnoverOptions}
        placeholder="-Choose an option-"
        value={effectiveSelectedBand}
        onValueChange={handleBandChange}
      />
    </div>
  );
};
